import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Copy, Check, ArrowRight } from 'lucide-react';
import { getActivePopupForStorefront, getVouchers, PopupCampaign, Voucher } from '../utils/api';
import { useVouchers } from '../context/VoucherContext';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// Countdown timer hook for product launch type
function useCountdown(targetDate: string | null | undefined) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });

  useEffect(() => {
    if (!targetDate) return;
    const target = new Date(targetDate).getTime();
    if (isNaN(target)) return;

    const calc = () => {
      const now = Date.now();
      const diff = target - now;
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });
        return false; // stop
      }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
        expired: false,
      });
      return true;
    };

    calc();
    const interval = setInterval(() => {
      if (!calc()) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  return timeLeft;
}

export default function VoucherPromoPopup() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { applyVoucher } = useVouchers();
  const { isLoggedIn } = useAuth();

  const [campaign, setCampaign] = useState<PopupCampaign | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);

  const countdown = useCountdown(campaign?.type === 'launch' ? campaign.launch_date : null);

  useEffect(() => {
    // Never show on admin or critical flow pages
    if (
      pathname.startsWith('/admin') ||
      pathname.startsWith('/checkout') ||
      pathname.startsWith('/payment')
    ) {
      return;
    }

    let isMounted = true;
    let timer: NodeJS.Timeout | null = null;

    const checkAndTrigger = async () => {
      try {
        const data = await getActivePopupForStorefront();
        if (!isMounted || !data) return;

        // Check target page
        const target = data.target_page || 'home';
        if (target === 'home' && pathname !== '/') return;
        if (target === 'shop' && !pathname.startsWith('/shop')) return;

        // Check active days of week
        const today = new Date().getDay();
        if (data.active_days && Array.isArray(data.active_days) && data.active_days.length > 0) {
          if (!data.active_days.includes(today)) return;
        }

        // Frequency check (localStorage)
        const lastSeen = localStorage.getItem(`secera_popup_seen_${data.id}`);
        if (lastSeen) {
          const hoursSince = (Date.now() - parseInt(lastSeen, 10)) / (1000 * 60 * 60);
          const freqHours = data.frequency_hours ?? 24;
          if (freqHours > 0 && hoursSince < freqHours) {
            return;
          }
        }

        setCampaign(data);

        // Prefetch vouchers if promo type
        if (data.type === 'promo' && data.voucher_code) {
          try {
            const activeList = await getVouchers();
            if (isMounted) setVouchers(activeList);
          } catch { /* non-blocking */ }
        }

        // Show after delay
        const delayMs = Math.max(500, (data.delay_seconds ?? 3) * 1000);
        timer = setTimeout(() => {
          if (isMounted) {
            setIsOpen(true);
            localStorage.setItem(`secera_popup_seen_${data.id}`, Date.now().toString());
          }
        }, delayMs);

      } catch (err) {
        console.error('Popup init error', err);
      }
    };

    checkAndTrigger();

    return () => {
      isMounted = false;
      if (timer) clearTimeout(timer);
    };
  }, [pathname]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    if (campaign) {
      localStorage.setItem(`secera_popup_seen_${campaign.id}`, Date.now().toString());
    }
  }, [campaign]);

  const handleCTA = useCallback(() => {
    if (!campaign) return;

    if (campaign.type === 'promo') {
      if (campaign.voucher_code) {
        // Copy voucher code
        navigator.clipboard.writeText(campaign.voucher_code);
        setCopied(true);

        // Auto-apply to cart
        const matched = vouchers.find(v => v.code === campaign.voucher_code || v.id === campaign.voucher_id);
        if (matched) {
          applyVoucher(matched);
          const isMemberPending = matched.conditions?.registered_only && !isLoggedIn;
          if (isMemberPending) {
            toast(`🔐 Voucher ${campaign.voucher_code} tersimpan! Masuk akun saat checkout untuk mengaktifkannya.`, {
              duration: 5000,
              style: {
                background: '#722f38',
                color: '#fff',
                borderRadius: '0',
                fontSize: '10px',
                fontWeight: 'bold',
                letterSpacing: '0.05em'
              }
            });
          } else {
            toast.success(`🎉 Voucher ${campaign.voucher_code} diklaim & terpasang di keranjang!`, { duration: 4000 });
          }
        } else {
          toast.success(`🎉 Kode ${campaign.voucher_code} berhasil disalin!`, { duration: 3000 });
        }
      }

      // Redirection for promo type if cta_url is configured
      if (campaign.cta_url) {
        if (campaign.cta_url.startsWith('http')) {
          window.open(campaign.cta_url, '_blank');
        } else {
          navigate(campaign.cta_url);
        }
      }
    } else if (campaign.type === 'banner' && campaign.cta_url) {
      // Navigate to CTA URL
      if (campaign.cta_url.startsWith('http')) {
        window.open(campaign.cta_url, '_blank');
      } else {
        navigate(campaign.cta_url);
      }
    } else if (campaign.type === 'launch' && campaign.cta_url) {
      if (campaign.cta_url.startsWith('http')) {
        window.open(campaign.cta_url, '_blank');
      } else {
        navigate(campaign.cta_url);
      }
    } else {
      toast.success('Selamat berbelanja!', { duration: 2500 });
    }

    handleClose();
  }, [campaign, vouchers, applyVoucher, navigate, handleClose, isLoggedIn]);

  if (!isOpen || !campaign) return null;

  const typeLabel = campaign.type === 'promo' ? 'PROMO' : campaign.type === 'launch' ? 'LAUNCH' : 'INFO';

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 select-none">

        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm cursor-pointer"
        />

        {/* Modal Card — Eclaté-inspired clean white card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 16 }}
          transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          className="relative w-full max-w-[420px] max-h-[92vh] overflow-y-auto bg-white rounded-[24px] sm:rounded-[28px] shadow-2xl z-10 flex flex-col mx-auto"
        >
          {/* Top Bar (White Header above image) */}
          <div className="px-6 pt-5 pb-3.5 flex items-center justify-between bg-white shrink-0">
            <div className="bg-white/95 text-[#722F38] text-label px-3 py-1 border border-[#722F38]/20 tracking-[0.2em] font-bold text-[10px] uppercase shadow-xs">
              {typeLabel}
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-800 transition-colors p-1 -mr-1 rounded-lg flex items-center justify-center"
              aria-label="Tutup"
            >
              <X size={18} />
            </button>
          </div>

          {/* Banner Image — Dynamic aspect ratio matching campaign choice */}
          {campaign.image_url ? (
            <div
              className="w-full bg-[#FAF9F7] overflow-hidden flex items-center justify-center shrink-0"
              style={{
                aspectRatio: campaign.image_aspect_ratio === '1:1' ? '1 / 1' : campaign.image_aspect_ratio === '4:5' ? '4 / 5' : '3 / 2'
              }}
            >
              <img
                src={campaign.image_url}
                alt={campaign.name || 'Promo'}
                className="w-full h-full object-cover block"
              />
            </div>
          ) : (
            <div
              className="w-full bg-gradient-to-br from-[#722F38] via-[#5a252d] to-[#3a151b] flex items-center justify-center shrink-0"
              style={{
                aspectRatio: campaign.image_aspect_ratio === '1:1' ? '1 / 1' : campaign.image_aspect_ratio === '4:5' ? '4 / 5' : '3 / 2'
              }}
            >
              <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-white/60">
                SECERA
              </span>
            </div>
          )}

          {/* Content Body — Left-aligned with gap-1 */}
          <div className="px-6 sm:px-7 pt-5 pb-6 flex flex-col items-start text-left gap-1">

            {/* Title — rendered as rich HTML with pink highlight pill */}
            {campaign.title ? (
              <h3
                className="text-2xl sm:text-[26px] font-extrabold text-gray-950 tracking-tight leading-[1.25] [&_mark]:bg-[#FFE4EE] [&_mark]:text-[#9D174D] [&_mark]:px-2.5 [&_mark]:py-0.5 [&_mark]:rounded-full [&_mark]:font-bold [&_mark]:inline-block [&_mark]:mx-0.5"
                dangerouslySetInnerHTML={{ __html: campaign.title }}
              />
            ) : (
              <h3 className="text-2xl font-extrabold text-gray-950 tracking-tight leading-[1.25]">Penawaran Spesial</h3>
            )}

            {/* Subtitle — rendered as rich HTML */}
            {campaign.subtitle && (
              <p
                className="text-sm sm:text-[14px] text-gray-600 font-normal leading-relaxed [&_mark]:bg-[#FFE4EE] [&_mark]:text-[#9D174D] [&_mark]:px-2 [&_mark]:py-0.5 [&_mark]:rounded-full [&_mark]:font-bold [&_mark]:inline-block"
                dangerouslySetInnerHTML={{ __html: campaign.subtitle }}
              />
            )}

            {/* Promo: Voucher code pill */}
            {campaign.type === 'promo' && campaign.voucher_code && (
              <div className="flex items-center gap-2 bg-gray-50 border border-dashed border-gray-300 px-3.5 py-1.5 rounded-xl w-fit mt-1">
                <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-400">Kode:</span>
                <span className="font-mono font-black text-sm text-[#722F38] tracking-widest">{campaign.voucher_code}</span>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(campaign.voucher_code!);
                    setCopied(true);
                    toast.success('Kode disalin!', { duration: 1500 });
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className="p-1 hover:bg-gray-200 rounded-md text-gray-500 transition-colors"
                  title="Salin"
                >
                  {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                </button>
              </div>
            )}

            {/* Launch: Countdown timer */}
            {campaign.type === 'launch' && campaign.launch_date && !countdown.expired && (
              <div className="space-y-2 py-1 mt-1">
                <div className="flex items-center gap-3">
                  {[
                    { val: countdown.days, label: 'Hari' },
                    { val: countdown.hours, label: 'Jam' },
                    { val: countdown.minutes, label: 'Min' },
                    { val: countdown.seconds, label: 'Det' },
                  ].map(({ val, label }) => (
                    <div key={label} className="flex flex-col items-center">
                      <span className="text-2xl font-black text-gray-900 tabular-nums leading-none">
                        {String(val).padStart(2, '0')}
                      </span>
                      <span className="text-[8px] uppercase tracking-[0.15em] text-gray-400 font-bold mt-1">{label}</span>
                    </div>
                  ))}
                </div>
                {campaign.launch_message && (
                  <p className="text-xs text-gray-500 leading-relaxed">{campaign.launch_message}</p>
                )}
              </div>
            )}

            {/* CTA Button — Fullwidth, primary brand wine (#722F38) */}
            <div className="pt-3 w-full">
              <button
                onClick={handleCTA}
                className="w-full py-3.5 px-6 bg-[#722F38] hover:bg-[#5a252d] text-white text-xs sm:text-sm font-bold uppercase tracking-wider rounded-lg shadow-md shadow-[#722F38]/20 transition-all active:scale-[0.99] flex items-center justify-center gap-2 group"
              >
                <span>{campaign.cta_text || 'BELANJA SEKARANG'}</span>
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Dismiss link */}
            <button
              type="button"
              onClick={handleClose}
              className="text-[11px] text-gray-400 hover:text-gray-600 transition-colors pt-2 mx-auto"
            >
              Lewati penawaran ini
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
